import type { AbilityKey, Family, Monster, Move, Rarity, StatusEffect } from './types'
import { FAMILIES, RARITY_INFO } from './families'
import { ABILITY_POOL } from './abilities'
import { EFFECT_POOL_NORMAL, EFFECT_POOL_SPECIAL } from './statuses'

// Seeded PRNG (mulberry32) — la génération est 100% déterministe depuis le seed.
// Plus tard on-chain: le seed sera dérivé du hash de la transaction de mint.
export function mulberry32(a: number): () => number {
  return function () {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export function hashSeed(seed: string): number {
  let h = 2166136261
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

export function makeSeed(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(8))
  return '0x' + Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

function pick<T>(rng: () => number, arr: T[]): T {
  return arr[Math.floor(rng() * arr.length)]
}

function rollRarity(rng: () => number): Rarity {
  const roll = rng() * 100
  let acc = 0
  for (const r of ['legendary', 'epic', 'rare', 'common'] as Rarity[]) {
    acc += RARITY_INFO[r].weight
    if (roll < acc) return r
  }
  return 'common'
}

const NAME_SUFFIXES = [
  'gorn', 'zilla', 'byte', 'fang', 'krag', 'mote', 'wick', 'ling',
  'nox', 'pix', 'thar', 'oku', 'vex', 'dra', 'bump', 'zor',
]

const BASE_STATS = { hp: 270, attack: 54, defense: 50, speed: 52, energy: 30 }

// Borne HAUTE de la variance de puissance des attaques, par rareté (le plancher
// reste 0.9). Un commun tire sa puissance dans [0.9, 1.1]; un légendaire peut
// grimper jusqu'à ×1.8 — les attaques des raretés hautes « spikent » davantage.
export const ATTACK_VARIANCE_HI: Record<Rarity, number> = {
  common: 1.1,
  rare: 1.2,
  epic: 1.5,
  legendary: 1.8,
}

// Probabilité qu'un coup porte un effet de statut (élargie sur les spéciales et
// par rareté). Les raretés hautes ont donc aussi plus souvent un effet.
const EFFECT_BASE_CHANCE: Record<Rarity, number> = {
  common: 0.16,
  rare: 0.24,
  epic: 0.34,
  legendary: 0.44,
}

// Poids des métamorphoses possibles au mint [0×, 1×, 2×], par rareté. Un commun
// n'évolue souvent pas; un légendaire atteint presque toujours l'Apex.
const EVO_WEIGHTS: Record<Rarity, [number, number, number]> = {
  common: [40, 50, 10],
  rare: [15, 55, 30],
  epic: [5, 45, 50],
  legendary: [0, 30, 70],
}

function weightedIndex(rng: () => number, weights: number[]): number {
  const total = weights.reduce((a, b) => a + b, 0)
  let roll = rng() * total
  for (let i = 0; i < weights.length; i++) {
    if (roll < weights[i]) return i
    roll -= weights[i]
  }
  return weights.length - 1
}

/** Nombre de métamorphoses (0/1/2) tiré selon la rareté — consomme 1 draw rng. */
export function rollMaxEvolutions(rng: () => number, rarity: Rarity): 0 | 1 | 2 {
  return weightedIndex(rng, EVO_WEIGHTS[rarity]) as 0 | 1 | 2
}

/**
 * Fabrique un coup: puissance variée selon la rareté + effet de statut éventuel.
 * Utilisé pour les 2 coups de base (au mint) ET pour les coups appris au niveau
 * (avec un rng dérivé de seed+niveau) — même distribution, toujours déterministe.
 */
export function generateMove(
  rng: () => number,
  base: { name: string; power: number; cost?: number },
  kind: 'normal' | 'special',
  rarity: Rarity,
): Move {
  const hi = ATTACK_VARIANCE_HI[rarity]
  const powerMult = 0.9 + rng() * (hi - 0.9)
  const move: Move = {
    name: base.name,
    power: Math.max(1, Math.round(base.power * powerMult)),
    cost: kind === 'special' ? (base.cost ?? 40) : 0,
    kind,
    category: 'damage',
  }
  // effet de statut ? spéciales plus enclines à l'infliger
  const chance = EFFECT_BASE_CHANCE[rarity] * (kind === 'special' ? 1.6 : 1)
  if (rng() < chance) {
    const pool = kind === 'special' ? EFFECT_POOL_SPECIAL : EFFECT_POOL_NORMAL
    move.effect = pool[Math.floor(rng() * pool.length)] as StatusEffect
    move.effectChance = kind === 'special' ? 0.5 : 0.35
  }
  // Riders de DÉGÂTS (variété façon Pokémon) — restent des coups de dégâts, donc
  // valables en arène. La plupart des coups restent simples.
  const rr = rng()
  if (kind === 'special') {
    if (rr < 0.1) {
      move.power = Math.round(move.power * 1.35) // gros coup, mais imprécis
      move.accuracy = 0.8
    } else if (rr < 0.2) {
      move.hits = 2 + Math.floor(rng() * 2) // multi-hit 2-3
      move.power = Math.round(move.power * 0.42)
    } else if (rr < 0.3) {
      move.drain = 0.4 // vol de HP
      move.power = Math.round(move.power * 0.9)
    } else if (rr < 0.38) {
      move.recoil = 0.25 // contrecoup, en échange de puissance
      move.power = Math.round(move.power * 1.28)
    }
  } else {
    if (rr < 0.1) {
      move.hits = 2 + Math.floor(rng() * 2)
      move.power = Math.round(move.power * 0.5)
    } else if (rr < 0.18) {
      move.priority = 1 // coup rapide (frappe avant), plus faible
      move.power = Math.round(move.power * 0.85)
    } else if (rr < 0.26) {
      move.drain = 0.35
      move.power = Math.round(move.power * 0.9)
    }
  }
  return move
}

/** Ability d'un monstre — dérivée du seed (namespace ':ability', pool de sa famille). */
export function abilityOf(monster: Pick<Monster, 'seed' | 'family' | 'ability'>): AbilityKey {
  if (monster.ability) return monster.ability
  const rng = mulberry32(hashSeed(monster.seed + ':ability'))
  const pool = ABILITY_POOL[monster.family]
  return pool[Math.floor(rng() * pool.length)]
}

const FAM_LIST: Family[] = ['bitcoin', 'ethereum', 'base', 'defi', 'meme', 'stable']

/** Mint local avec VŒU de type secondaire: 'surprise' laisse faire le hasard,
 * null force un mono-type, une famille précise force la paire. On échantillonne
 * des seeds jusqu'à satisfaire le vœu — le monstre reste 100% dérivé du seed
 * (le résolveur et les replays reconstruisent exactement le même). */
export function generateMonsterForTypes(family: Family, wish: Family | null | 'surprise'): Monster {
  if (wish === 'surprise') return generateMonster(family)
  for (let i = 0; i < 4000; i++) {
    const seed = makeSeed()
    const sec = secondaryTypeOf({ seed, family })
    if (wish === null ? sec === undefined : sec === wish) return generateMonster(family, seed)
  }
  return generateMonster(family) // statistiquement inatteignable — jamais bloquant
}

/** Type SECONDAIRE éventuel — dérivé du seed (namespace ':type2'), ~32% des monstres. */
export function secondaryTypeOf(monster: Pick<Monster, 'seed' | 'family' | 'family2'>): Family | undefined {
  if (monster.family2 !== undefined) return monster.family2
  const rng = mulberry32(hashSeed(monster.seed + ':type2'))
  if (rng() >= 0.25) return undefined
  const others = FAM_LIST.filter((f) => f !== monster.family)
  return others[Math.floor(rng() * others.length)]
}

// Bibliothèque de coups UTILITAIRES (buff/debuff/soin/statut-pur + variété de
// dégâts). Réservée aux coups APPRIS (casual) — l'arène reste aux coups de base.
const UTILITY_MOVES: Move[] = [
  { name: 'Bull Run', power: 0, cost: 30, kind: 'special', category: 'buff', statChanges: [{ target: 'self', stat: 'attack', stages: 2 }] },
  { name: 'Overclock', power: 0, cost: 25, kind: 'special', category: 'buff', statChanges: [{ target: 'self', stat: 'speed', stages: 2 }] },
  { name: 'Fortify', power: 0, cost: 25, kind: 'special', category: 'buff', statChanges: [{ target: 'self', stat: 'defense', stages: 2 }] },
  { name: 'FUD', power: 0, cost: 25, kind: 'special', category: 'debuff', statChanges: [{ target: 'enemy', stat: 'attack', stages: -2 }] },
  { name: 'Rug Pull', power: 0, cost: 30, kind: 'special', category: 'debuff', statChanges: [{ target: 'enemy', stat: 'defense', stages: -2 }] },
  { name: 'Congestion', power: 0, cost: 25, kind: 'special', category: 'debuff', statChanges: [{ target: 'enemy', stat: 'speed', stages: -2 }] },
  { name: 'Rebase', power: 0, cost: 40, kind: 'special', category: 'heal', heal: 0.42 },
  { name: 'Buyback', power: 0, cost: 28, kind: 'special', category: 'heal', heal: 0.28 },
  { name: 'Cold Wallet', power: 0, cost: 35, kind: 'special', category: 'status', effect: 'freeze', effectChance: 0.85 },
  { name: 'Honeypot', power: 0, cost: 30, kind: 'special', category: 'status', effect: 'paralyze', effectChance: 0.9 },
  { name: 'Rug Sleep', power: 0, cost: 40, kind: 'special', category: 'status', effect: 'sleep', effectChance: 0.75 },
  { name: 'Toxic Yield', power: 0, cost: 28, kind: 'special', category: 'status', effect: 'poison', effectChance: 0.9 },
  { name: 'Liquidity Siphon', power: 62, cost: 34, kind: 'special', category: 'damage', drain: 0.5 },
  { name: 'Degen Yolo', power: 118, cost: 30, kind: 'special', category: 'damage', recoil: 0.28, accuracy: 0.9 },
  { name: 'Spam Bot', power: 24, cost: 30, kind: 'special', category: 'damage', hits: 3 },
  { name: 'Diamond Charge', power: 135, cost: 45, kind: 'special', category: 'damage', charge: true },
  { name: 'Front Run', power: 42, cost: 0, kind: 'normal', category: 'damage', priority: 2 },
  { name: 'Stealth Rug', power: 0, cost: 30, kind: 'special', category: 'debuff', hazard: 0.12 },
  { name: 'Toxic Spill', power: 0, cost: 28, kind: 'special', category: 'debuff', hazardPoison: true },
  { name: 'Sticky Web', power: 0, cost: 26, kind: 'special', category: 'debuff', hazardSlow: 1 },
  // --- profondeur 1v1 ---
  { name: 'Print Money', power: 0, cost: 25, kind: 'special', category: 'buff', setsWeather: 'bull' },
  { name: 'Market Crash', power: 0, cost: 25, kind: 'special', category: 'debuff', setsWeather: 'bear' },
  { name: 'Chaos Fork', power: 0, cost: 25, kind: 'special', category: 'buff', setsWeather: 'volatile' },
  { name: 'Firewall', power: 0, cost: 20, kind: 'special', category: 'buff', protect: true, priority: 4 },
  { name: 'Shill Storm', power: 0, cost: 25, kind: 'special', category: 'debuff', taunt: 3 },
  { name: 'Liquidation Cascade', power: 0, cost: 20, kind: 'special', category: 'damage', counter: 1.5, priority: -3 },
  { name: 'Decoy Wallet', power: 0, cost: 30, kind: 'special', category: 'buff', substitute: true },
  { name: 'Yield Parasite', power: 0, cost: 30, kind: 'special', category: 'debuff', leech: true },
  { name: 'Vesting', power: 0, cost: 35, kind: 'special', category: 'heal', wish: 0.5 },
  { name: 'Gaslight', power: 0, cost: 28, kind: 'special', category: 'status', confuse: 1 },
  { name: 'Shitpost Barrage', power: 54, cost: 30, kind: 'special', category: 'damage', confuse: 0.5 },
  { name: 'Flash Crash', power: 50, cost: 25, kind: 'special', category: 'damage', flinch: 0.5, priority: 1 },
  { name: 'Focus Feed', power: 0, cost: 20, kind: 'special', category: 'buff', statChanges: [{ target: 'self', stat: 'accuracy', stages: 2 }] },
  { name: 'Smoke Screen', power: 0, cost: 22, kind: 'special', category: 'debuff', statChanges: [{ target: 'enemy', stat: 'accuracy', stages: -2 }] },
  { name: 'Ghost Chain', power: 0, cost: 22, kind: 'special', category: 'buff', statChanges: [{ target: 'self', stat: 'evasion', stages: 2 }] },
  { name: 'Rapid Bridge', power: 45, cost: 24, kind: 'special', category: 'damage', clearHazard: true },
]

function cloneMove(m: Move): Move {
  return { ...m, statChanges: m.statChanges?.map((s) => ({ ...s })) }
}

/**
 * Catalogue du TUTEUR de coups — tous les coups enseignables directement (choix
 * du joueur, pas de random) : utilitaires + coups de la famille. Casual only.
 */
export function tutorCatalog(family: Family, rarity: Rarity): Move[] {
  const def = FAMILIES[family]
  const famDmg: Move[] = [
    ...def.normals.map((n) => ({ name: n.name, power: n.power, cost: 0, kind: 'normal' as const, category: 'damage' as const })),
    ...def.specials.map((s) => ({ name: s.name, power: s.power, cost: s.cost, kind: 'special' as const, category: 'damage' as const })),
  ]
  // les coups de dégâts de famille profitent quand même de la variance de rareté
  const hi = ATTACK_VARIANCE_HI[rarity]
  for (const m of famDmg) m.power = Math.max(1, Math.round(m.power * (0.95 + (hi - 0.95) * 0.5)))
  return [...famDmg, ...UTILITY_MOVES.map(cloneMove)]
}

export function generateMonster(family: Family, seed?: string, forceRarity?: Rarity): Monster {
  const s = seed ?? makeSeed()
  const rng = mulberry32(hashSeed(s))
  const def = FAMILIES[family]
  // Le tirage de rareté est TOUJOURS consommé pour garder les draws suivants
  // identiques — un monstre forcé (Recycleur) et un monstre naturel de même
  // seed ne diffèrent que par la rareté et ce qui en découle.
  const rolled = rollRarity(rng)
  const rarity = forceRarity ?? rolled
  const mult = RARITY_INFO[rarity].mult
  const maxEvolutions = rollMaxEvolutions(rng, rarity)

  const vary = () => 0.9 + rng() * 0.2

  const stats = {
    hp: Math.round(BASE_STATS.hp * def.statBias.hp * mult * vary()),
    attack: Math.round(BASE_STATS.attack * def.statBias.attack * mult * vary()),
    defense: Math.round(BASE_STATS.defense * def.statBias.defense * mult * vary()),
    speed: Math.round(BASE_STATS.speed * def.statBias.speed * mult * vary()),
    energy: Math.round(BASE_STATS.energy * def.statBias.energy * mult * vary()),
  }

  const moves: Move[] = [
    generateMove(rng, pick(rng, def.normals), 'normal', rarity),
    generateMove(rng, pick(rng, def.specials), 'special', rarity),
  ]

  const name = pick(rng, def.namePrefixes) + pick(rng, NAME_SUFFIXES)

  return {
    id: s,
    name,
    family,
    family2: secondaryTypeOf({ seed: s, family }),
    rarity,
    seed: s,
    stats,
    moves,
    maxEvolutions,
    ability: abilityOf({ seed: s, family }),
    createdAt: Date.now(),
  }
}

// ---------------------------------------------------------------------------
// NORMALISATION CLASSÉE (audit phase 3.1) — retire l'avantage de rareté sur
// l'axe COMPÉTITIF (stats + puissance des coups) tout en gardant l'IDENTITÉ
// (nom, look, ability, type secondaire, effets, riders, et la rareté elle-même
// pour le prestige/visuel). Fonction PURE et déterministe: client et résolveur
// l'appliquent à l'identique, donc le replay classé se vérifie toujours.
//
//   « La rareté apporte du prestige et des visuels, pas 89-100% de winrate. »
// ---------------------------------------------------------------------------

/** Puissance canonique d'un coup de base (sans variance de rareté), par nom. */
function canonicalBasePower(family: Family, moveName: string): number | null {
  const def = FAMILIES[family]
  const hit = [...def.normals, ...def.specials].find((m) => m.name === moveName)
  return hit ? hit.power : null
}

/** Reconstitue le multiplicateur des RIDERS de dégâts appliqué par generateMove
 * (constantes fixes détectées via les flags): permet de rebaser la puissance
 * sur la base canonique en préservant le caractère du coup (multi, drain…). */
function riderPowerMult(move: Move): number {
  if (move.category && move.category !== 'damage') return 1
  if (move.kind === 'special') {
    if (move.accuracy === 0.8) return 1.35 // gros coup imprécis
    if (move.hits) return 0.42 // multi-hit
    if (move.drain) return 0.9
    if (move.recoil) return 1.28
  } else {
    if (move.hits) return 0.5
    if (move.priority === 1) return 0.85 // coup rapide
    if (move.drain) return 0.9
  }
  return 1
}

/** Applique la normalisation classée à un monstre déjà généré. */
export function normalizeForRanked(m: Monster): Monster {
  const mult = RARITY_INFO[m.rarity]?.mult ?? 1
  const norm = (v: number) => Math.max(1, Math.round(v / mult))
  return {
    ...m,
    stats: {
      hp: norm(m.stats.hp),
      attack: norm(m.stats.attack),
      defense: norm(m.stats.defense),
      speed: norm(m.stats.speed),
      energy: norm(m.stats.energy),
    },
    moves: m.moves.map((mv) => {
      let out = mv
      // Pression de statut: les raretés hautes portent plus SOUVENT un effet.
      // On ne retire pas l'effet (identité), on ramène sa probabilité de
      // déclenchement à l'espérance d'un commun — l'effet reste, mais son
      // avantage systématique disparaît.
      if (mv.effect && mv.effectChance) {
        const factor = EFFECT_BASE_CHANCE.common / (EFFECT_BASE_CHANCE[m.rarity] ?? EFFECT_BASE_CHANCE.common)
        out = { ...out, effectChance: Math.min(1, mv.effectChance * factor) }
      }
      if ((out.category ?? 'damage') !== 'damage') return out
      const base = canonicalBasePower(m.family, out.name)
      if (base === null) return out // coup appris hors catalogue de base → inchangé
      return { ...out, power: Math.max(1, Math.round(base * riderPowerMult(out))) }
    }),
  }
}

/** Monstre classé directement depuis (famille, seed): génère puis normalise —
 * le raccourci utilisé par le client ET le résolveur en arène. */
export function generateRankedMonster(family: Family, seed: string): Monster {
  return normalizeForRanked(generateMonster(family, seed))
}

/**
 * Coup APPRENABLE proposé à un monstre à un niveau donné — déterministe (dérivé
 * de seed + niveau), donc l'offre est stable et identique pour un monstre donné.
 * `premium` = coup d'ÉVOLUTION: plus puissant que d'habitude, effet garanti.
 */
export function generateLearnableMove(
  family: Family,
  rarity: Rarity,
  seed: string,
  level: number,
  premium = false,
): Move {
  const ns = premium ? 'evolve' : 'learn'
  const rng = mulberry32(hashSeed(`${seed}:${ns}:${level}`))
  const def = FAMILIES[family]

  // ~40-45% des coups appris sont UTILITAIRES (buff/debuff/soin/statut/variété).
  if (rng() < (premium ? 0.4 : 0.45)) {
    const m = cloneMove(UTILITY_MOVES[Math.floor(rng() * UTILITY_MOVES.length)])
    if (premium) {
      // Évolution: buffs/debuffs plus forts, soins plus grands, dégâts +18%.
      if (m.statChanges) m.statChanges = m.statChanges.map((s) => ({ ...s, stages: s.stages + (s.stages > 0 ? 1 : -1) }))
      if (m.heal) m.heal = Math.min(0.6, m.heal + 0.12)
      if (m.power) m.power = Math.round(m.power * 1.18)
      if (m.effectChance) m.effectChance = Math.min(1, m.effectChance + 0.1)
    }
    return m
  }

  // Sinon: un coup de dégâts classique (avec ses riders éventuels).
  const kind: 'normal' | 'special' = rng() < (premium ? 0.7 : 0.55) ? 'special' : 'normal'
  const pool = kind === 'special' ? def.specials : def.normals
  const move = generateMove(rng, pick(rng, pool), kind, rarity)
  if (premium) {
    move.power = Math.round(move.power * 1.18)
    if (!move.effect) {
      const p = kind === 'special' ? EFFECT_POOL_SPECIAL : EFFECT_POOL_NORMAL
      move.effect = p[Math.floor(rng() * p.length)] as StatusEffect
      move.effectChance = kind === 'special' ? 0.6 : 0.45
    }
  }
  return move
}

export function generateOpponent(playerRarity: Rarity): Monster {
  const families = Object.keys(FAMILIES) as Family[]
  const family = families[Math.floor(Math.random() * families.length)]
  // Appariement équitable: l'adversaire reste à ±1 cran de rareté du joueur —
  // ni beaucoup plus fort, ni beaucoup plus faible.
  const order: Rarity[] = ['common', 'rare', 'epic', 'legendary']
  const idx = order.indexOf(playerRarity)
  const minIdx = Math.max(idx - 1, 0)
  const maxIdx = Math.min(idx + 1, order.length - 1)
  for (let i = 0; i < 60; i++) {
    const m = generateMonster(family)
    const r = order.indexOf(m.rarity)
    if (r >= minIdx && r <= maxIdx) return m
  }
  return generateMonster(family)
}
